import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { propertyFormSchema } from "@/modules/Properties/schema/propertyFormSchema";
import { Activity, Building, ClipboardList, DollarSign, Home, Ruler } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const propertyTypes = ["apartment", "house", "office", "commercial", "land"] as const;
const propertyStatuses = ["available", "occupied", "sold", "pending", "maintenance"] as const;

interface PropertyMainInfoProps {
    form: UseFormReturn<z.infer<typeof propertyFormSchema>>;
    isEditing: boolean;
}

export const PropertyMainInfo = ({ form, isEditing }: PropertyMainInfoProps) => {
    const { t } = useTranslation();

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg font-medium flex items-center gap-2">
                    <Building className="h-5 w-5 text-primary" />
                    {t("properties.sections.mainInfo")}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-2">
                                <Home className="h-4 w-4 text-muted-foreground" />
                                {t("properties.form.name")}
                            </FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Input
                                        {...field}
                                        disabled={!isEditing}
                                        placeholder={t("properties.form.namePlaceholder") || "Enter property name"}

                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-2">
                                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                                {t("properties.form.description")}
                            </FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Textarea
                                        {...field}
                                        disabled={!isEditing}
                                        placeholder={t("properties.form.descriptionPlaceholder") || "Enter property description"}
                                        className="min-h-[100px]"
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="grid grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="type"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                    <Building className="h-4 w-4 text-muted-foreground" />
                                    {t("properties.form.type")}
                                </FormLabel>
                                <Select
                                    disabled={!isEditing}
                                    onValueChange={field.onChange}
                                    defaultValue={field.value}
                                >
                                    <FormControl>
                                        <SelectTrigger >
                                            <SelectValue defaultValue={field.value}>
                                                {field.value
                                                    ? t(`properties.type.${field.value}`)
                                                    : t("properties.form.typePlaceholder")}
                                            </SelectValue>
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {propertyTypes.map((type) => (
                                            <SelectItem key={type} value={type}>
                                                {t(`properties.type.${type}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                    <Activity className="h-4 w-4 text-muted-foreground" />
                                    {t("properties.form.status")}
                                </FormLabel>
                                <Select
                                    disabled={!isEditing}
                                    onValueChange={field.onChange}
                                    defaultValue={field.value}
                                >
                                    <FormControl>
                                        <SelectTrigger >
                                            <SelectValue defaultValue={field.value}>
                                                {field.value
                                                    ? t(`properties.status.${field.value}`)
                                                    : t("properties.form.statusPlaceholder")}
                                            </SelectValue>
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {propertyStatuses.map((status) => (
                                            <SelectItem key={status} value={status}>
                                                {t(`properties.status.${status}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="price"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                                    {t("properties.form.price")}
                                </FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input
                                            {...field}
                                            type="number"
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.pricePlaceholder") || "Enter price"}

                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                        />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="area"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                    <Ruler className="h-4 w-4 text-muted-foreground" />
                                    {t("properties.form.area")}
                                </FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input
                                            {...field}
                                            type="number"
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.areaPlaceholder") || "Enter area"}

                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                        />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            </CardContent>
        </Card>
    );
}; 